import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, register, loginWithGoogle, registerWithGoogle } from '../clients/grpc';
import { LoginRequest, RegisterRequest, LoginWithGoogleRequest, RegisterWithGoogleRequest } from '../generated/v1alpha1/auth_pb';
import './AuthPage.css';
import { AuthContext, User } from '../contexts/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';

// import DarkModeToggle from './DarkModeToggle';

interface AuthPageProps {
  onLogin: (token: string, user?: User) => void;
}

const AuthPage: React.FC<AuthPageProps> = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setAccessToken, setUser } = useContext(AuthContext);

  const handleLogin = async () => {
    const req = new LoginRequest();
    req.setEmail(email);
    req.setPassword(password);
    const res = await login(req);
    if (res.getSuccess()) {
      setMessage('Login successful!');
      const token = res.getAccessToken();
      const userPb = res.getUser();
      let userData: User | undefined;
      if (userPb) {
        userData = {
          id: userPb.getId(),
          email: userPb.getEmail(),
          firstName: userPb.getFirstName(),
          lastName: userPb.getLastName(),
          role: userPb.getRole(),
        };
      }
      setAccessToken(token);
      setUser(userData || null);
      onLogin(token, userData);
      navigate('/');
    } else {
      setError(res.getMessage());
    }
  };

  const handleRegister = async () => {
    const req = new RegisterRequest();
    req.setEmail(email);
    req.setPassword(password);
    const res = await register(req);
    if (res.getSuccess()) {
      setMessage('Registration successful! Logging you in...');
      await handleLogin();
    } else {
      setError(res.getMessage());
    }
  };

  const handleGoogleFlow = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError(null);
      setMessage(null);
      try {
        // If the user is on the 'Register' tab
        if (!isLogin) {
          const registerReq = new RegisterWithGoogleRequest();
          registerReq.setGoogleAccessToken(tokenResponse.access_token);
          const registerRes = await registerWithGoogle(registerReq);
          if (!registerRes.getSuccess()) {
            setError(registerRes.getMessage());
            return;
          }
          setMessage('Registration successful! Logging you in...');
        }

        // Login with Google (either after registration or directly)
        const loginReq = new LoginWithGoogleRequest();
        loginReq.setGoogleAccessToken(tokenResponse.access_token);
        const loginRes = await loginWithGoogle(loginReq);
        if (loginRes.getSuccess()) {
          setMessage('Login successful!');
          const token = loginRes.getAccessToken();
          const userPb = loginRes.getUser();
          let userData: User | undefined;
          if (userPb) {
            userData = {
              id: userPb.getId(),
              email: userPb.getEmail(),
              firstName: userPb.getFirstName(),
              lastName: userPb.getLastName(),
              role: userPb.getRole(),
            };
          }
          setAccessToken(token);
          setUser(userData || null);
          onLogin(token, userData);
          navigate('/');
        } else {
          setError(loginRes.getMessage());
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error occurred');
        }
      }
    },
    onError: () => {
      setError('Google login failed');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    try {
      if (isLogin) {
        await handleLogin();
      } else {
        await handleRegister();
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred');
      }
    }
  };

  return (
    <>
      <header className="header">
        <div className="container header-container">
          <div className="logo-container">
            <img src={`${process.env.PUBLIC_URL}/squaresircle.png`} alt="SquareSircle Logo" className="logo-image" />
            <h1 className="logo-text">SquareSircle</h1>
          </div>
          {/*<DarkModeToggle />*/}
        </div>
      </header>
      <div className="auth-container">
        <div className="auth-form">
          <h2>{isLogin ? 'Login' : 'Register'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit">{isLogin ? 'Login' : 'Register'}</button>
          </form>
          <div className="divider">OR</div>
          <button onClick={() => handleGoogleFlow()} className="google-login-button">
            {isLogin ? 'Login with Google' : 'Register with Google'}
          </button>
          {error && <p className="error">{error}</p>}
          {message && <p className="message">{message}</p>}
          <p>
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
            <button onClick={() => setIsLogin(!isLogin)} className="toggle-button">
              {isLogin ? 'Register' : 'Login'}
            </button>
          </p>
        </div>
      </div>
    </>
  );
};

export default AuthPage;